import {
  type CanvasElementFrom,
  type HeightSettings,
  type IoConfig,
  type PartialPropsOf,
  type PropsOf,
  type WithIoConfig,
  type WithProps,
} from '@blaze-runtime/engine-domain';
import { type FontWeightOption } from '@blaze-shared/api-contracts-domain';
import {
  type ElementSizingMode,
  getActiveBinding,
  type PropertiesConfig,
  type TableColumnFormat,
  TEXT_INPUT_FORMATS,
} from '@blaze-shared/elements-domain';
import { type ValueWithUnitOfMeasure } from '@blaze-shared/shared-util';
import { getValueByPath } from '@blaze-shared/workflow-domain';

import { FormLikeFieldValidationHandler } from '../../canvas-elements/io-data/default-handlers/formlike-field-validation-handler.service';
import { registerElementDefinition } from '../../services/element-definitions.service';
import {
  validateFontSizeWithLineHeight,
  validateLineHeightWithFontSize,
} from '../../utils/elements/validation.utils';
import { propsConfigHelper as epc } from '../../utils/elements-props-config.helpers';

import { TextInputDefinitionMapper } from './ai/text-input.definition-mapper';
import { TextInputElementHandler } from './ai/text-input.element-handler';
import { generateTextInputFormField } from './generation/form-field';
import { textInputV2Migration } from './migrations/002-text-input.migration';
import { textInputV3Migration } from './migrations/003-text-input.migration';
import { textInputV4PaddingToMarginMigration } from './migrations/004-text-input-padding-to-margin.migration';
import { TextInputOutputEmitGuard } from './text-input-output-emit.guard';
import { TextInputComponent } from './text-input.component';
import { type TextInputPropsModel } from './text-input.models';
import { TextInputPropsBuilder } from './text-input.props-builder';

export { transformToTextInput } from './text-input.transforms';
export type TextInputElement = CanvasElementFrom<'textInput'>;
export type TextInputDef = WithProps<TextInputPropsModel> & WithIoConfig;

// Extend the CanvasElementDefinitions type
declare module '@blaze-runtime/engine-domain' {
  interface CanvasElementDefinitions {
    textInput: TextInputDef;
  }
}

const DEFAULT_WIDTH = 356;
const DEFAULT_HEIGHT = 40;
const DEFAULT_HEIGHT_LABEL_TOP = 72;
const MIN_SIZE = 40;
const MAX_WIDTH = 900;
const MAX_HEIGHT = 150;
const WIDTH_OPTIONS: ElementSizingMode[] = ['fixed', 'full'];
const HEIGHT_OPTIONS: ElementSizingMode[] = ['min', 'fixed', 'auto'];

registerElementDefinition({
  type: 'textInput',
  componentType: TextInputComponent,
  outputEmitGuard: TextInputOutputEmitGuard,
  validationHandler: FormLikeFieldValidationHandler,
  isListed: true,
  name: 'Text Input',
  width: { mode: 'value', value: DEFAULT_WIDTH, fixed: true },
  height: { mode: 'value', value: DEFAULT_HEIGHT, fixed: false },
  ioConfig: getIoConfig(),
  propsConfig: getPropsConfig(),
  defaults: getDefaults(),
  initial: getInitial(),
  propsBuilder: TextInputPropsBuilder,
  generation: {
    formField: generateTextInputFormField,
  },
  groups: ['custom'],
  styleIncludePaths: [
    'style',
    'content.title.align',
    'content.title.iconPosition',
    'content.title.position',
    'content.title.width',
  ],
  metaUiFeatures: {
    type: 'input',
    paths: {
      format: 'content.input.format',
      precision: 'content.input.precision',
      currency: 'content.input.currency',
      duration: 'content.input.duration',
      dateFormat: 'content.input.dateFormat',
      dateTimeFormat: 'content.input.dateTimeFormat',
      readonly: 'content.input.readonly',
      placeholder: 'content.input.placeholder',
    },
  },
  features: {
    searchAndFiltersFieldSupport: true,
    ai: {
      optionalInput: true,
    },
  },
  ai: {
    name: 'textInput',
    handler: () => TextInputElementHandler,
    definitionMapper: () => TextInputDefinitionMapper,
  },

  version: 4,
  migrations: {
    2: textInputV2Migration,
    3: textInputV3Migration,
    4: textInputV4PaddingToMarginMigration,
  },
});

function getIoConfig(): IoConfig {
  return {
    inputs: [
      {
        name: 'value',
        isDefaultDataSourceInput: true,
        isDefaultDataValueInput: true,
        bindingDefs: [{ property: 'value', label: 'Input value', dataType: 'String' }],
      },
      {
        name: 'visible',
        bindingDefs: [{ property: 'visible', label: 'Is Visible', dataType: 'Boolean' }],
      },
    ],
    outputs: [
      {
        eventType: 'change',
        debounce: 500,
        schema: {
          // While the default data type is a String, this can change later to a Number if a format of the filed changes
          columns: [
            {
              columnType: 'String',
              dataType: 'String',
              isProfilePicture: false,
              name: '', // for non-object value schema, this should be empty
            },
          ],
          id: '',
          name: 'Text Input',
        },
      },
      {
        eventType: 'submit',
        schema: {
          columns: [
            {
              columnType: 'String',
              dataType: 'String',
              isProfilePicture: false,
              name: '', // for non-object value schema, this should be empty
            },
          ],
          id: '',
          name: 'Text Input',
        },
      },
    ],
  };
}

function getDefaults(): PropsOf<'textInput'> {
  return {
    version: 1,
    content: {
      title: {
        label: 'Text Input',
        position: 'left',
        align: { horizontal: 'left' },
        width: '30%',
        iconColor: { color1: '$t.title', selectorType: 'solid' },
      },
      input: {
        placeholder: '',
        format: 'text',
        readonly: false,
        precision: 0,
        currency: 'USD',
        duration: 'h:mm',
        maximum: undefined,
        minimum: undefined,
      },
      container: {
        overflow: 'auto',
      },
    },
    style: {
      fontSize: '$t.fontSize',
      fontFamily: '$t.fontFamily',
      lineHeight: '$t.lineHeight',
      general: {
        title: { color1: '$t.title', selectorType: 'solid' },
        accent: { color1: '$t.accent', selectorType: 'solid' },
        offset: { margin: '$t.spacingInput' },
      },
      labelBlock: {
        fontFamily: '$t.fontFamily',
        fontSize: '$t.fontSize',
        fontStyle: [],
        fontWeight: '$t.labelFontWeight',
        iconSize: '16px',
        lineHeight: '$t.lineHeight',
      },
      inputFont: {
        style: [],
        fontFamily: '$t.fontFamily',
        fontSize: '$t.fontSize',
        lineHeight: '$t.lineHeight',
        fontWeight: '$t.fontWeight' as FontWeightOption,
      },
      input: {
        value: { color1: '$t.value', selectorType: 'solid' },
        fill: { color1: '$t.fillInternal', selectorType: 'solid' },
        border: { color1: '$t.outline', selectorType: 'solid' },
        radius: '$t.radius',
      },
    },
  };
}

function getInitial(): PartialPropsOf<'textInput'> {
  return {
    version: 2,
    content: {
      title: {
        width: '35%',
      },
    },
  };
}

function getPropsConfig(): PropertiesConfig {
  return epc.initConfig('textInput', [
    epc.section('content', 'Content', [
      epc.subsections.subsection(
        'content.input',
        'Input',
        [
          epc.utils.conditionalField(
            epc.utils.observedField(
              epc.fields.textInputFormatField('content.input.format', 'Format'),
              (model: TextInputPropsModel, format: TableColumnFormat | undefined) => {
                if (model.content.input.format === format) {
                  return {};
                }
                return { 'root.height.fixed': format === 'paragraph', 'root.height.mode': 'value' };
              },
            ),
            'content.input.format',
            (_, model) => {
              return (
                getActiveBinding(model.data)?.dataSource?.columns?.[0]?.format?.formatType ===
                'SecureText'
              );
            },
          ),
          epc.utils.conditionalField(
            epc.fields.selectFieldFromList('content.input.format', 'Format', TEXT_INPUT_FORMATS, {
              disableWhen: () => 'Secure text modification disabled',
            }),
            'content.input.format',
            (_, model) => {
              return (
                getActiveBinding(model.data)?.dataSource?.columns?.[0]?.format?.formatType !==
                'SecureText'
              );
            },
          ),
          epc.fields.textInput('content.input.placeholder', 'Placeholder'),
          epc.utils.conditionalField(
            epc.fields.numberPrecisionField('content.input.precision'),
            'content.input.format',
            (value: TableColumnFormat) =>
              value !== 'number' && value !== 'percentage' && value !== 'currency',
          ),
          epc.fields.currencyConditionalField('content.input.currency', 'content.input.format'),
          epc.utils.conditionalField(
            epc.fields.textInput('content.input.minimum', 'Minimum', { inputType: 'number' }),
            'content.input.format',
            (format: TableColumnFormat, model) =>
              (format !== 'number' && format !== 'percentage' && format !== 'currency') ||
              !getActiveBinding(model.data),
          ),
          epc.utils.conditionalField(
            epc.fields.textInput('content.input.maximum', 'Maximum', { inputType: 'number' }),
            'content.input.format',
            (format: TableColumnFormat, model) =>
              (format !== 'number' && format !== 'percentage' && format !== 'currency') ||
              !getActiveBinding(model.data),
          ),
          epc.utils.conditionalField(
            epc.fields.durationFormatField(`content.input.duration`, 'Duration format', {
              defaultValue: 'h:mm',
              allowNone: false,
            }),
            `content.input.format`,
            (format: TableColumnFormat) => format !== 'duration',
          ),
          epc.utils.observedField(
            epc.fields.toggleField('content.input.readonly', 'Read Only'),
            (_, readonly: boolean) => {
              return {
                ['style.input.border.color1']: readonly ? 'transparent' : '$t.outline',
                ['style.input.border.selectorType']: 'solid',
                ['style.input.fill.color1']: readonly ? 'transparent' : '$t.fillInternal',
              };
            },
          ),
        ],
        {
          dataBindings: [
            {
              key: 'value',
              label: 'Value',
              sources: 'field',
              bindings: [{ property: 'value', label: 'Input value', dataType: 'String' }],
            },
          ],
        },
      ),

      epc.subsections.subsection('content.container', 'Size', [
        epc.root.elementWidthField(
          { min: MIN_SIZE, max: MAX_WIDTH, unit: 'px', step: 1 },
          WIDTH_OPTIONS,
        ),
        epc.root.elementHeightField(
          { min: MIN_SIZE, max: MAX_HEIGHT, unit: 'px', step: 1 },
          HEIGHT_OPTIONS,
          (model, options) => {
            const format = getValueByPath(model, 'content.input.format') as
              | TableColumnFormat
              | undefined;
            return format === 'paragraph' ? options : [];
          },
        ),
        epc.utils.conditionalField(
          epc.fields.contentOverflowField('content.container.overflow', 'Text behavior'),
          'content.input.format',
          (format, model) => {
            const isNotParagraph = (format as TableColumnFormat | undefined) !== 'paragraph';
            const isNotFixed =
              (getValueByPath(model, 'root.height') as HeightSettings)?.fixed === false;
            return isNotParagraph || isNotFixed;
          },
        ),
      ]),

      epc.subsections.visibilitySubsection(),
      epc.subsections.labelContentSubsection('content'),
    ]),
    epc.section(
      'style',
      'Style',
      [
        epc.subsections.styleSheetSubsection(),
        epc.subsections.subsection('style.general', 'Margin', [
          epc.fields.spacingAndAlignField('style.general.offset', {
            hasMargin: true,
          }),
        ]),

        epc.subsections.subsection('style.general', 'General', [
          epc.fields.colorField('style.general.accent', 'Accent', {
            allowGradient: false,
            allowNone: false,
          }),
        ]),

        epc.subsections.subsection('style.inputFont', 'Text Input Font', [
          ...epc.fields.fontFamilyAndWeightFields(
            'style.inputFont.fontFamily',
            'style.inputFont.fontWeight',
          ),
          epc.fields.fontStyleField('style.inputFont.style', 'Style', ['bold']),
          epc.utils.observedField(
            epc.fields.sliderField(`style.inputFont.fontSize`, 'Size', [
              { min: 8, max: 72, unit: 'px' },
            ]),
            (props: TextInputPropsModel, value: ValueWithUnitOfMeasure) => {
              return validateLineHeightWithFontSize(props, value, 'style.inputFont.lineHeight');
            },
          ),
          epc.utils.conditionalField(
            epc.utils.observedField(
              epc.fields.sliderField(`style.inputFont.lineHeight`, 'Line height', [
                { min: 8, max: 72, unit: 'px' },
              ]),
              (props: TextInputPropsModel, value: ValueWithUnitOfMeasure) => {
                return validateFontSizeWithLineHeight(props, value, 'style.inputFont.fontSize');
              },
            ),
            'content.input.format',
            (format: TableColumnFormat | undefined) => format !== 'paragraph',
          ),
        ]),

        epc.subsections.subsection('style.input', 'Text Input Style', [
          epc.fields.colorField('style.input.value', 'Value', {
            allowGradient: false,
            allowNone: false,
          }),
          epc.fields.colorField('style.input.fill', 'Fill', { allowNone: false }),
          epc.fields.colorField('style.input.border', 'Border', { allowGradient: false }),
          epc.fields.radiusField('style.input.radius'),
        ]),

        epc.subsections.labelStyleSubsection(
          {
            color: 'style.general.title',
            enabled: 'content.title.enabled',
            fontFamily: 'style.labelBlock.fontFamily',
            fontSize: 'style.labelBlock.fontSize',
            fontStyle: 'style.labelBlock.fontStyle',
            fontWeight: 'style.labelBlock.fontWeight',
            horizontalAlign: 'content.title.align',
            icon: 'content.title.icon',
            iconColor: 'content.title.iconColor',
            iconPosition: 'content.title.iconPosition',
            iconSize: 'style.labelBlock.iconSize',
            lineHeight: 'style.labelBlock.lineHeight',
            position: 'content.title.position',
            width: 'content.title.width',
          },
          {
            excludedPositions: ['right'],
            overrides: {
              horizontalAlign: epc.fields.alignField('content.title.align', 'Align', {
                defaultValue: { horizontal: 'left', vertical: 'middle' },
                excludedOptions: ['center'],
                vertical: (model) =>
                  getValueByPath(model, 'content.title.position') === 'left' &&
                  (getValueByPath(model, 'content.input.format') as
                    | TableColumnFormat
                    | undefined) === 'paragraph',
              }),
              position: epc.fields.inputTitlePositionField(
                'content.title.position',
                DEFAULT_HEIGHT,
                DEFAULT_HEIGHT_LABEL_TOP,
                'style.general.offset.margin',
              ),
            },
          },
        ),
      ],
      true,
    ),
  ]);
}
